import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { KeyboardShortcutsModal } from '../components/layout/KeyboardShortcutsModal';
import { PageTransition } from '../components/layout/PageTransition';
import { TopLoadingBar } from '../components/layout/TopLoadingBar';
import { CommandPalette, useCommandPalette } from '../components/ui/command-palette';
import { DialogContent, DialogDescription, DialogRoot, DialogTitle } from '../components/ui/dialog';
import { ReportLibraryPanel } from '../features/analytics/ReportLibraryPanel';
import { OnboardingBanner } from '../features/onboarding/components/onboarding-banner';
import { Sidebar } from '../layouts/Sidebar';
import { Topbar } from '../layouts/Topbar';
import { WorkspaceSidebarProvider } from '../layouts/workspace-sidebar-context';
import { decodeJwtPayload } from '../lib/auth';
import { useMeQuery } from '../lib/query-hooks';
import { cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth-store';

const SIDEBAR_KEY = 'engage-sidebar-collapsed';

export function AuthLayout(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((state) => state.token);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const user = useAuthStore((state) => state.user);
  const orgId = useAuthStore((state) => state.orgId);
  const role = useAuthStore((state) => state.role);
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const meQuery = useMeQuery();

  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  const [analyticsCustomizerOpen, setAnalyticsCustomizerOpen] = React.useState(false);
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();
  const decodedToken = React.useMemo(() => (token ? decodeJwtPayload(token) : null), [token]);
  const effectiveOrgId = orgId ?? decodedToken?.orgId ?? null;

  // Sequence tracking for two-key shortcuts (G+D, N+C, etc.)
  const pendingKey = React.useRef<string | null>(null);
  const pendingTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  function toggleSidebar(): void {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }

  // Bootstrap user from /me response
  React.useEffect(() => {
    if (!token || !meQuery.data || user) return;
    setAuth({
      user: meQuery.data,
      token,
      refreshToken,
      orgId: decodedToken?.orgId ?? orgId,
      role: decodedToken?.role ?? role,
    });
  }, [decodedToken, meQuery.data, orgId, refreshToken, role, setAuth, token, user]);

  React.useEffect(() => {
    if (!token || user || !meQuery.isError) return;
    clearAuth();
    navigate('/login', { replace: true });
  }, [clearAuth, meQuery.isError, navigate, token, user]);

  // Global keyboard shortcuts
  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      const tag = (e.target as HTMLElement).tagName;
      const editing = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable;

      // ⌘/ — toggle sidebar (always active)
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        toggleSidebar();
        return;
      }

      // ? — show shortcuts (skip when editing)
      if (!editing && e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // Two-key sequences (skip when editing or palette open)
      if (editing || paletteOpen) return;

      const key = e.key.toUpperCase();

      if (pendingKey.current) {
        const first = pendingKey.current;
        pendingKey.current = null;
        if (pendingTimer.current) clearTimeout(pendingTimer.current);

        if (first === 'G') {
          if (key === 'D') { navigate('/dashboard'); return; }
          if (key === 'C') { navigate('/campaigns'); return; }
          if (key === 'A') { navigate('/automations'); return; }
          if (key === 'S') { navigate('/segments'); return; }
        }
        if (first === 'N') {
          if (key === 'C') { navigate('/campaigns/new'); return; }
          if (key === 'A') { navigate('/automations/new'); return; }
        }
        return;
      }

      if (key === 'G' || key === 'N') {
        pendingKey.current = key;
        pendingTimer.current = setTimeout(() => { pendingKey.current = null; }, 800);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (pendingTimer.current) clearTimeout(pendingTimer.current);
    };
  }, [navigate, paletteOpen]);

  React.useEffect(() => {
    if (!location.pathname.startsWith('/analytics')) setAnalyticsCustomizerOpen(false);
  }, [location.pathname]);

  if (token && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base text-sm font-medium text-text-tertiary">
        Loading workspace…
      </div>
    );
  }

  return (
    <WorkspaceSidebarProvider value={{
      analyticsCustomizerOpen,
      openAnalyticsCustomizer: () => setAnalyticsCustomizerOpen(true),
      closeAnalyticsCustomizer: () => setAnalyticsCustomizerOpen(false),
    }}>
      <div className={cn('flex h-screen overflow-hidden bg-bg-base')}>
        {/* Sidebar */}
        <div className="hidden shrink-0 md:block">
          <Sidebar
            collapsed={collapsed}
            onToggle={toggleSidebar}
            onOpenShortcuts={() => setShortcutsOpen(true)}
          />
        </div>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Topbar
            onOpenCommandPalette={() => setPaletteOpen(true)}
            onOpenShortcuts={() => setShortcutsOpen(true)}
          />
          <main className="flex-1 overflow-y-auto flex flex-col">
            <OnboardingBanner orgId={effectiveOrgId} />
            <div className="flex-1 p-3 sm:p-6">
              <TopLoadingBar />
              <PageTransition>
                <Outlet />
              </PageTransition>
            </div>
          </main>
        </div>
      </div>

      {/* Overlays */}
      <DialogRoot open={analyticsCustomizerOpen} onOpenChange={setAnalyticsCustomizerOpen}>
        <DialogContent
          hideClose
          size="sm"
          titleId="analytics-customizer-title"
          descriptionId="analytics-customizer-description"
          overlayClassName="bg-black/20 backdrop-blur-none"
          className="!bottom-0 !left-auto !right-0 !top-0 !h-screen !w-full !max-w-[360px] !translate-x-0 !translate-y-0 !rounded-none !p-0"
        >
          <DialogTitle id="analytics-customizer-title" className="sr-only">Customize report</DialogTitle>
          <DialogDescription id="analytics-customizer-description" className="sr-only">Add, remove, and arrange report cards on the analytics dashboard.</DialogDescription>
          <ReportLibraryPanel mode="drawer" onClose={() => setAnalyticsCustomizerOpen(false)} />
        </DialogContent>
      </DialogRoot>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <KeyboardShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </WorkspaceSidebarProvider>
  );
}
